import {
  Circle,
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import akanthasLogo from "../../../static/assets/logo_rvg.png";
import akanthasLogoWhite from "../../../static/assets/logo_rvg_white.png";
import mascotCover from "../../../static/assets/mascot-1.png";
import mascotClosing from "../../../static/assets/mascot-2.png";
import star0 from "../../../static/assets/0-stars-white.png";
import star1 from "../../../static/assets/1-stars-white.png";
import star2 from "../../../static/assets/2-stars-white.png";
import star3 from "../../../static/assets/3-stars-white.png";
import ShowClientLogo from "../../../components/download/wasteQuality/ClientLogo";
import { ImageComponent } from "../../../components/download/wasteQuality/ImageComponent";
import {
  formattedDate,
  formattedDateDocument,
  formattedTime,
} from "../../../components/formatted/FormattedDateAndTime";
import { starsCalculation } from "../../activity/ActivityUtilities";
import { FILLING_RATE_OBJECTIVE } from "../utils/collectionInsights";
import { formattedWasteColor } from "../../../components/formatted/FormattedText";
import { MaterialHorizontalBarChart } from "./weeklyReportCharts";
import {
  computeUndesirableRate,
  getZoomBox,
  isExemplarySortingFlux,
  materialsFromAnomalies,
  roundPercent,
  selectExampleAnomalies,
  slicesWithOthers,
} from "./weeklyReportModel";
import MulishLight from "../../../static/fonts/Mulish/Mulish-Light.ttf";
import MulishMedium from "../../../static/fonts/Mulish/Mulish-Medium.ttf";
import MulishBold from "../../../static/fonts/Mulish/Mulish-Bold.ttf";
import MulishBlack from "../../../static/fonts/Mulish/Mulish-Black.ttf";

Font.register({
  family: "Mulish",
  fonts: [
    { src: MulishLight, fontWeight: 200 },
    { src: MulishMedium, fontWeight: 500 },
    { src: MulishBold, fontWeight: 700 },
    { src: MulishBlack, fontWeight: 900 },
  ],
});

const COLORS = {
  navy: "#0F265C",
  teal: "#00A7A6",
  yellow: "#F8AA00",
  muted: "#8A8A8A",
  line: "#E5E5E5",
  softTeal: "#E0F5F4",
  white: "#FFFFFF",
};

const STAR_IMAGES = [star0, star1, star2, star3];

// A4 landscape (72 dpi) — même MediaBox sur toutes les pages.
const PAGE_SIZE = { width: 841.89, height: 595.28 };
const PAGE_FIXED_SIZE_STYLE = {
  width: PAGE_SIZE.width,
  height: PAGE_SIZE.height,
  minHeight: PAGE_SIZE.height,
  maxHeight: PAGE_SIZE.height,
};
const FIXED_PAGE_PROPS = { size: PAGE_SIZE, wrap: false };

const PHOTOS_PER_GRID_PAGE = 3;
const GRID_PHOTO_WIDTH = 230;
const GRID_PHOTO_HEIGHT = 250;

const styles = StyleSheet.create({
  page: {
    ...PAGE_FIXED_SIZE_STYLE,
    fontFamily: "Mulish",
    backgroundColor: COLORS.white,
    paddingTop: 28,
    paddingHorizontal: 40,
    paddingBottom: 68,
  },
  pageShell: {
    ...PAGE_FIXED_SIZE_STYLE,
    flexDirection: "column",
    position: "relative",
  },
  closingPageShell: {
    flex: 1,
    width: PAGE_SIZE.width - 88,
    height: PAGE_SIZE.height - 50,
    justifyContent: "space-between",
  },
  pageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 18,
  },
  sectionLabel: {
    color: COLORS.teal,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  pageTitle: {
    color: COLORS.navy,
    fontSize: 22,
    fontWeight: 700,
    maxWidth: 480,
  },
  headerRight: {
    alignItems: "flex-end",
  },
  headerClientLogoBox: {
    width: 72,
    height: 40,
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 6,
    padding: 3,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
  },
  headerClientLogo: {
    width: 64,
    height: 32,
    objectFit: "contain",
  },
  headerClientPlaceholder: {
    color: COLORS.muted,
    fontSize: 7,
    textAlign: "center",
  },
  periodLabel: {
    color: "#5A6A85",
    fontSize: 9,
  },
  footer: {
    position: "absolute",
    bottom: 14,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerText: {
    color: COLORS.muted,
    fontSize: 8,
    maxWidth: 240,
  },
  footerAkanthasLogo: {
    width: 70,
    objectFit: "contain",
  },
  twoCol: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 14,
    marginBottom: 6,
  },
  card: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 14,
    backgroundColor: COLORS.white,
  },
  chartCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 16,
    backgroundColor: COLORS.white,
    minHeight: 260,
    justifyContent: "space-between",
  },
  cardTitle: {
    color: COLORS.navy,
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
  },
  chartHint: {
    color: COLORS.muted,
    fontSize: 8,
    marginTop: 10,
  },
  muted: {
    color: COLORS.muted,
    fontSize: 8,
  },
  body: {
    color: COLORS.navy,
    fontSize: 9,
    lineHeight: 1.4,
  },
  bullet: {
    color: COLORS.navy,
    fontSize: 9,
    marginBottom: 4,
    paddingLeft: 2,
  },
  statsBlock: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 16,
    backgroundColor: COLORS.white,
    minHeight: 280,
  },
  statsTitle: {
    color: COLORS.navy,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
  },
  statsSubtitle: {
    color: COLORS.muted,
    fontSize: 9,
    marginBottom: 14,
  },
  fluxStatRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: "#F7F9FC",
    borderRadius: 8,
  },
  fluxStatDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  fluxStatName: {
    flex: 1,
    color: COLORS.navy,
    fontSize: 10,
    fontWeight: 500,
  },
  fluxStatValue: {
    color: COLORS.navy,
    fontSize: 11,
    fontWeight: 700,
  },
  conclusion: {
    marginTop: 16,
    color: COLORS.navy,
    fontSize: 10,
    lineHeight: 1.45,
    marginBottom: 4,
  },
  conclusionLabel: {
    fontWeight: 700,
    color: COLORS.navy,
    fontSize: 11,
    marginBottom: 6,
  },
  coverPage: {
    ...PAGE_FIXED_SIZE_STYLE,
    fontFamily: "Mulish",
    backgroundColor: COLORS.navy,
    paddingTop: 0,
    paddingBottom: 0,
    paddingHorizontal: 0,
  },
  coverLayout: {
    flex: 1,
    flexDirection: "row",
    width: PAGE_SIZE.width,
    height: PAGE_SIZE.height,
  },
  coverIllustrationCol: {
    width: "38%",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingBottom: 0,
  },
  coverIllustration: {
    width: 320,
    height: 460,
    objectFit: "contain",
  },
  coverRightCol: {
    flex: 1,
    paddingTop: 36,
    paddingRight: 40,
    paddingBottom: 28,
    paddingLeft: 12,
    justifyContent: "space-between",
  },
  coverTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 100,
    width: "100%",
  },
  coverAkanthasLogo: {
    width: 110,
    objectFit: "contain",
  },
  coverClientBox: {
    width: 100,
    height: 100,
    backgroundColor: COLORS.white,
    borderRadius: 10,
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  coverClientPlaceholder: {
    color: COLORS.navy,
    fontSize: 9,
    textAlign: "center",
  },
  coverClientLogo: {
    width: 92,
    height: 92,
    objectFit: "contain",
  },
  coverContentCol: {
    flexGrow: 1,
    justifyContent: "center",
    paddingVertical: 24,
  },
  coverType: {
    color: COLORS.yellow,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    marginBottom: 14,
    textTransform: "uppercase",
  },
  coverTitle: {
    color: COLORS.white,
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 14,
    maxWidth: 420,
    lineHeight: 1.2,
  },
  coverSubtitle: {
    color: "#A0B2C6",
    fontSize: 13,
    marginBottom: 22,
    maxWidth: 420,
  },
  coverPill: {
    backgroundColor: COLORS.yellow,
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 18,
    alignSelf: "flex-start",
  },
  coverPillText: {
    color: COLORS.navy,
    fontSize: 11,
    fontWeight: 700,
  },
  coverFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-end",
  },
  coverFooterText: {
    color: "#94A3B8",
    fontSize: 8,
  },
  metaRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 20,
  },
  metaCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 12,
    minHeight: 72,
    justifyContent: "flex-start",
  },
  metaLabel: {
    color: COLORS.teal,
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 8,
  },
  metaValue: {
    color: COLORS.navy,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.35,
  },
  summaryBox: {
    backgroundColor: COLORS.softTeal,
    borderRadius: 12,
    paddingVertical: 22,
    paddingHorizontal: 22,
    minHeight: 200,
  },
  summaryTitle: {
    color: COLORS.teal,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 12,
  },
  summaryBody: {
    color: COLORS.navy,
    fontSize: 11,
    lineHeight: 1.55,
    marginBottom: 8,
  },
  chapterPage: {
    ...PAGE_FIXED_SIZE_STYLE,
    fontFamily: "Mulish",
    backgroundColor: "#FFB300",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 56,
    paddingRight: 0,
  },
  chapterContent: {
    flex: 1,
    justifyContent: "center",
    maxWidth: 460,
    paddingRight: 40,
  },
  chapterNumber: {
    color: COLORS.navy,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    marginBottom: 14,
  },
  chapterTitle: {
    color: COLORS.navy,
    fontSize: 36,
    fontWeight: 700,
    marginBottom: 14,
    lineHeight: 1.15,
  },
  chapterDesc: {
    color: COLORS.navy,
    fontSize: 13,
    maxWidth: 420,
    lineHeight: 1.45,
    fontWeight: 500,
  },
  chapterLogo: {
    position: "absolute",
    left: 56,
    bottom: 32,
    width: 80,
    objectFit: "contain",
  },
  cornerDecor: {
    position: "absolute",
    right: -80,
    bottom: -80,
    width: 260,
    height: 260,
  },
  kpiBox: {
    flexGrow: 1,
    alignSelf: "stretch",
    minHeight: 280,
    backgroundColor: "#FFB300",
    borderRadius: 12,
    paddingVertical: 20,
    paddingLeft: 12,
    paddingRight: 10,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  kpiMain: {
    alignItems: "center",
    alignSelf: "flex-start",
  },
  kpiValue: {
    color: COLORS.navy,
    fontSize: 40,
    fontWeight: 700,
    marginBottom: 4,
    textAlign: "center",
  },
  kpiLabel: {
    color: COLORS.navy,
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1.35,
    textAlign: "center",
    maxWidth: 160,
  },
  kpiQualitySection: {
    marginTop: 12,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "rgba(15, 38, 92, 0.2)",
    alignItems: "center",
    alignSelf: "flex-start",
    width: 160,
  },
  qualityLabel: {
    color: COLORS.navy,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 8,
    textAlign: "center",
  },
  qualityStarsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 8,
  },
  starsImage: {
    width: 96,
    height: 22,
    objectFit: "contain",
  },
  qualityLevel: {
    color: COLORS.navy,
    fontSize: 11,
    fontWeight: 700,
  },
  qualityHint: {
    color: "rgba(15, 38, 92, 0.7)",
    fontSize: 8,
    lineHeight: 1.4,
    textAlign: "center",
  },
  distributionCard: {
    flex: 2.2,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 16,
    minHeight: 280,
    backgroundColor: COLORS.white,
    justifyContent: "center",
  },
  exemplaryCard: {
    flex: 2.2,
    borderRadius: 12,
    padding: 16,
    minHeight: 280,
    backgroundColor: COLORS.navy,
    justifyContent: "center",
    alignItems: "center",
  },
  exemplaryStars: {
    width: 120,
    height: 28,
    objectFit: "contain",
    marginBottom: 14,
  },
  exemplaryHeading: {
    color: COLORS.white,
    fontSize: 20,
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 10,
  },
  exemplaryBody: {
    color: "#A0B2C6",
    fontSize: 11,
    lineHeight: 1.5,
    textAlign: "center",
    maxWidth: 380,
  },
  legendCol: {
    flex: 1,
    paddingLeft: 8,
  },
  legendRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  legendDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    marginRight: 8,
  },
  legendText: {
    color: COLORS.navy,
    fontSize: 10,
    flex: 1,
  },
  sideStack: {
    flex: 0.95,
    alignSelf: "stretch",
    flexDirection: "column",
  },
  equalCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 14,
    backgroundColor: COLORS.white,
    minHeight: 280,
  },
  characterizationChartCard: {
    flexGrow: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 14,
    backgroundColor: COLORS.white,
    justifyContent: "flex-start",
    marginBottom: 4,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#EEF1F5",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 4,
  },
  tableHeaderCell: {
    color: COLORS.navy,
    fontSize: 9,
    fontWeight: 700,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  tableCell: {
    color: COLORS.navy,
    fontSize: 9,
  },
  colType: { width: "70%" },
  colPercent: { width: "30%" },
  chartHintRight: {
    color: COLORS.muted,
    fontSize: 8,
    marginTop: 12,
    textAlign: "right",
  },
  photoMain: {
    flexGrow: 1,
    flexDirection: "row",
    gap: 12,
    minHeight: 280,
    alignItems: "stretch",
    marginBottom: 4,
  },
  photoMainImage: {
    flex: 1,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.line,
    justifyContent: "center",
    alignItems: "center",
  },
  photoZoomColumn: {
    width: 150,
    justifyContent: "space-between",
    gap: 8,
  },
  photoZoomWrap: {
    flex: 1,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 96,
  },
  pageTitleFlux: {
    color: COLORS.navy,
    fontSize: 20,
    fontWeight: 700,
    marginTop: 2,
  },
  photoMetaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 10,
    marginBottom: 8,
    gap: 24,
  },
  photoUndesirable: {
    flex: 1,
    color: COLORS.navy,
    fontSize: 10,
    lineHeight: 1.4,
  },
  photoAction: {
    color: COLORS.muted,
    fontSize: 10,
    textAlign: "right",
    maxWidth: 260,
  },
  dateBadge: {
    backgroundColor: COLORS.navy,
    borderRadius: 16,
    paddingVertical: 7,
    paddingHorizontal: 14,
    alignSelf: "flex-start",
  },
  dateBadgeText: {
    color: COLORS.white,
    fontSize: 9,
    fontWeight: 700,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: 12,
    alignItems: "stretch",
    marginBottom: 4,
  },
  gridCard: {
    width: "32.5%",
    height: 340,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.line,
    padding: 8,
    paddingBottom: 10,
    alignItems: "center",
    justifyContent: "flex-start",
  },
  gridCardEmpty: {
    width: "32.5%",
    height: 340,
  },
  gridPhotoArea: {
    width: GRID_PHOTO_WIDTH,
    height: GRID_PHOTO_HEIGHT,
    borderRadius: 6,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: COLORS.white,
  },
  gridCaption: {
    color: COLORS.navy,
    fontSize: 10,
    fontWeight: 700,
    marginTop: 6,
    textAlign: "center",
  },
  gridCaptionMuted: {
    color: COLORS.muted,
    fontSize: 8,
    marginTop: 2,
    textAlign: "center",
  },
  keyPage: {
    ...PAGE_FIXED_SIZE_STYLE,
    fontFamily: "Mulish",
    backgroundColor: COLORS.navy,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 56,
    paddingRight: 0,
  },
  keyPageShell: {
    ...PAGE_FIXED_SIZE_STYLE,
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    paddingLeft: 0,
    paddingRight: 40,
  },
  keyContent: {
    maxWidth: 460,
    paddingTop: 48,
  },
  keyLabel: {
    color: COLORS.teal,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    marginBottom: 22,
  },
  keyValue: {
    color: "#FFB300",
    fontSize: 72,
    fontWeight: 700,
    marginBottom: 20,
  },
  keyDesc: {
    color: COLORS.white,
    fontSize: 13,
    maxWidth: 420,
    lineHeight: 1.5,
    fontWeight: 500,
  },
  keyLogo: {
    position: "absolute",
    left: 56,
    bottom: 56,
  },
  keyLogoImage: {
    width: 70,
    objectFit: "contain",
  },
  closingPage: {
    ...PAGE_FIXED_SIZE_STYLE,
    fontFamily: "Mulish",
    backgroundColor: COLORS.navy,
    paddingTop: 28,
    paddingBottom: 22,
    paddingLeft: 48,
    paddingRight: 40,
  },
  closingTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 100,
    width: "100%",
    marginBottom: 8,
  },
  closingClientBox: {
    width: 100,
    height: 100,
    backgroundColor: COLORS.white,
    borderRadius: 10,
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  closingClientLogo: {
    width: 92,
    height: 92,
    objectFit: "contain",
  },
  closingAkanthasLogo: {
    width: 110,
    objectFit: "contain",
  },
  closingBottom: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-end",
  },
  closingBody: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  closingContent: {
    flex: 1,
    paddingRight: 24,
    justifyContent: "center",
  },
  closingSlogan: {
    color: "#FFB300",
    fontSize: 22,
    fontWeight: 700,
    maxWidth: 400,
    lineHeight: 1.35,
    marginBottom: 28,
  },
  closingContact: {
    color: COLORS.white,
    fontSize: 11,
    marginBottom: 7,
  },
  closingUrl: {
    color: "#FFB300",
    fontSize: 13,
    fontWeight: 700,
    marginTop: 12,
  },
  closingIllustrationCol: {
    width: "42%",
    alignItems: "center",
    justifyContent: "flex-end",
    height: "100%",
  },
  closingIllustration: {
    width: 300,
    height: 400,
    objectFit: "contain",
  },
  closingFooter: {
    color: "rgba(255,255,255,0.45)",
    fontSize: 8,
    marginTop: 8,
  },
});

const periodRangeLabel = (periodStart, periodStop) =>
  `${formattedDateDocument(periodStart)} – ${formattedDateDocument(periodStop)}`;

const ReportHeader = ({
  t,
  section,
  title,
  periodStart,
  periodStop,
  corporationLogoUrl,
  showPeriod = true,
}) => (
  <View style={styles.pageHeader}>
    <View>
      <Text style={styles.sectionLabel}>{section}</Text>
      <Text style={styles.pageTitle}>{title}</Text>
    </View>
    <View style={styles.headerRight}>
      <View style={styles.headerClientLogoBox}>
        {corporationLogoUrl ? (
          ShowClientLogo(corporationLogoUrl, styles.headerClientLogo)
        ) : (
          <Text style={styles.headerClientPlaceholder}>
            {t("weeklyReport.clientLogoPlaceholder")}
          </Text>
        )}
      </View>
      {showPeriod ? (
        <Text style={styles.periodLabel}>
          {t("weeklyReport.periodHeader", {
            period: periodRangeLabel(periodStart, periodStop),
          })}
        </Text>
      ) : null}
    </View>
  </View>
);

const ReportFooter = ({ t, siteName, flux }) => (
  <View style={styles.footer}>
    <Text style={styles.footerText}>{t("Confidential")}</Text>
    <Image src={akanthasLogo} style={styles.footerAkanthasLogo} />
    <Text style={styles.footerText}>
      {[siteName, flux].filter(Boolean).join(" · ")}
    </Text>
  </View>
);

const CornerDecor = ({
  outerFill,
  innerFill,
  size = 260,
  innerRadius = 48,
}) => {
  const center = size / 2;
  return (
    <View style={styles.cornerDecor}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle cx={center} cy={center} r={center} fill={outerFill} />
        <Circle cx={center} cy={center} r={innerRadius} fill={innerFill} />
      </Svg>
    </View>
  );
};

const ChapterDivider = ({ chapter, title, description }) => (
  <Page {...FIXED_PAGE_PROPS} style={styles.chapterPage}>
    <View style={styles.pageShell}>
      <View style={styles.chapterContent}>
        <Text style={styles.chapterNumber}>{chapter}</Text>
        <Text style={styles.chapterTitle}>{title}</Text>
        {description ? (
          <Text style={styles.chapterDesc}>{description}</Text>
        ) : null}
      </View>
      <Image src={akanthasLogo} style={styles.chapterLogo} />
      <CornerDecor outerFill={COLORS.navy} innerFill={COLORS.teal} />
    </View>
  </Page>
);

const CoverPage = ({
  t,
  corporationLogoUrl,
  corporationName,
  siteName,
  fluxLabels = [],
  weekNumber,
  year,
  periodStart,
  periodStop,
}) => (
  <Page {...FIXED_PAGE_PROPS} style={styles.coverPage}>
    <View style={styles.coverLayout}>
      <View style={styles.coverIllustrationCol}>
        <Image src={mascotCover} style={styles.coverIllustration} />
      </View>
      <View style={styles.coverRightCol}>
        <View style={styles.coverTop}>
          <Image src={akanthasLogoWhite} style={styles.coverAkanthasLogo} />
          <View style={styles.coverClientBox}>
            {corporationLogoUrl ? (
              ShowClientLogo(corporationLogoUrl, styles.coverClientLogo)
            ) : (
              <Text style={styles.coverClientPlaceholder}>
                {t("weeklyReport.clientLogoPlaceholder")}
              </Text>
            )}
          </View>
        </View>
        <View style={styles.coverContentCol}>
          <Text style={styles.coverType}>
            {t("weeklyReport.coverType", { week: weekNumber, year })}
          </Text>
          <Text style={styles.coverTitle}>{t("weeklyReport.coverTitle")}</Text>
          <Text style={styles.coverSubtitle}>
            {[siteName || corporationName, fluxLabels.join(", ")]
              .filter(Boolean)
              .join(" — ")}
          </Text>
          <View style={styles.coverPill}>
            <Text style={styles.coverPillText}>
              {periodRangeLabel(periodStart, periodStop)}
            </Text>
          </View>
        </View>
        <View style={styles.coverFooter}>
          <Text style={styles.coverFooterText}>
            {t("weeklyReport.editedOn", {
              date: formattedDateDocument(new Date().toISOString()),
            })}
          </Text>
        </View>
      </View>
    </View>
  </Page>
);

const buildPeriodSummaryParagraphs = (t, model) => {
  const paragraphs = [];
  const rotationsByFlux = model.rotationsByFlux || [];
  const fillingStats = model.fillingStats || [];
  const sortingErrors = model.sortingErrors || [];
  const compositions = model.compositions || [];

  const rotationSentence = t("weeklyReport.summaryRotations", {
    count: model.totalRotations || 0,
  });
  if (rotationsByFlux.length > 0) {
    const details = rotationsByFlux
      .map((item) =>
        t("weeklyReport.summaryRotationFluxPart", {
          count: item.count,
          flux: item.flux,
        }),
      )
      .join(", ");
    paragraphs.push(
      `${rotationSentence} ${t("weeklyReport.summaryRotationsBreakdown", {
        details,
      })}`,
    );
  } else {
    paragraphs.push(rotationSentence);
  }

  if (fillingStats.length > 0) {
    const overallAvg = roundPercent(
      fillingStats.reduce((sum, item) => sum + item.avg, 0) /
        fillingStats.length,
    );
    paragraphs.push(
      t("weeklyReport.summaryFillingAverage", { avg: overallAvg }),
    );
    if (model.fillingGood) {
      paragraphs.push(t("weeklyReport.fillingGood"));
    } else {
      const weak = fillingStats.filter(
        (item) => item.avg < FILLING_RATE_OBJECTIVE,
      );
      weak.forEach((item) => {
        paragraphs.push(
          t("weeklyReport.fillingBelow", {
            flux: item.flux,
            average: roundPercent(item.avg),
            objective: FILLING_RATE_OBJECTIVE,
            gap: roundPercent(FILLING_RATE_OBJECTIVE - item.avg),
          }),
        );
      });
    }
  }

  if (sortingErrors.length > 0) {
    sortingErrors.slice(0, 3).forEach((fluxErrors) => {
      const top = fluxErrors.slices?.[0];
      if (!top) return;
      paragraphs.push(
        t("weeklyReport.summaryTopError", {
          flux: fluxErrors.flux,
          material: t(top.material),
          percent:
            fluxErrors.total > 0
              ? roundPercent((top.count * 100) / fluxErrors.total)
              : 0,
        }),
      );
    });
  } else {
    paragraphs.push(t("weeklyReport.summaryNoErrors"));
  }

  const mainComposition = compositions[0];
  if (mainComposition?.slices?.[0]) {
    const top = mainComposition.slices[0];
    paragraphs.push(
      t("weeklyReport.summaryCompositionLead", {
        flux: mainComposition.flux,
        material: t(top.material),
        percent:
          mainComposition.total > 0
            ? roundPercent((top.count * 100) / mainComposition.total)
            : 0,
      }),
    );
  }

  return paragraphs.filter(Boolean);
};

const SynthesisPage = ({
  t,
  model,
  corporationName,
  corporationLogoUrl,
  siteName,
  periodStart,
  periodStop,
  fluxLabels,
}) => {
  const meta = [
    { label: t("weeklyReport.metaProducer"), value: corporationName || "—" },
    { label: t("weeklyReport.metaSite"), value: siteName || "—" },
    {
      label: t("weeklyReport.metaFlux"),
      lines: fluxLabels.length > 0 ? fluxLabels : ["—"],
    },
    {
      label: t("weeklyReport.metaPeriod"),
      value: periodRangeLabel(periodStart, periodStop),
    },
  ];
  const summaryParagraphs = buildPeriodSummaryParagraphs(t, model);
  return (
    <Page {...FIXED_PAGE_PROPS} style={styles.page}>
      <ReportHeader
        t={t}
        section={t("weeklyReport.sectionSynthesis")}
        title={t("weeklyReport.generalInfoTitle")}
        periodStart={periodStart}
        periodStop={periodStop}
        showPeriod={false}
        corporationLogoUrl={corporationLogoUrl}
      />
      <View style={styles.metaRow}>
        {meta.map((item) => (
          <View key={item.label} style={styles.metaCard}>
            <Text style={styles.metaLabel}>{item.label}</Text>
            {Array.isArray(item.lines) ? (
              item.lines.map((line) => (
                <Text key={line} style={styles.metaValue}>
                  {line}
                </Text>
              ))
            ) : (
              <Text style={styles.metaValue}>{item.value}</Text>
            )}
          </View>
        ))}
      </View>
      <View style={styles.summaryBox}>
        <Text style={styles.summaryTitle}>
          {t("weeklyReport.periodSummaryTitle")}
        </Text>
        {summaryParagraphs.map((paragraph) => (
          <Text key={paragraph} style={styles.summaryBody}>
            {paragraph}
          </Text>
        ))}
      </View>
      <ReportFooter
        t={t}
        siteName={siteName || corporationName}
        flux={fluxLabels.join(", ")}
      />
    </Page>
  );
};

const RotationsPage = ({
  t,
  model,
  corporationName,
  corporationLogoUrl,
  siteName,
  periodStart,
  periodStop,
  fluxLabels = [],
}) => {
  const { rotationsByFlux, fillingStats, fillingGood, totalRotations } = model;
  const overallFillingAvg =
    fillingStats.length > 0
      ? roundPercent(
          fillingStats.reduce((sum, item) => sum + item.avg, 0) /
            fillingStats.length,
        )
      : 0;
  const conclusion = fillingGood
    ? t("weeklyReport.fillingGood")
    : fillingStats
        .filter((item) => item.avg < FILLING_RATE_OBJECTIVE)
        .map((item) =>
          t("weeklyReport.fillingBelow", {
            flux: item.flux,
            average: roundPercent(item.avg),
            objective: FILLING_RATE_OBJECTIVE,
            gap: roundPercent(FILLING_RATE_OBJECTIVE - item.avg),
          }),
        )
        .join(" ") || t("No collection for the current period");

  return (
    <Page {...FIXED_PAGE_PROPS} style={styles.page}>
      <ReportHeader
        t={t}
        section={t("weeklyReport.sectionCollection")}
        title={t("weeklyReport.rotationsTitle")}
        periodStart={periodStart}
        periodStop={periodStop}
        corporationLogoUrl={corporationLogoUrl}
      />
      <View style={styles.twoCol}>
        <View style={styles.statsBlock}>
          <Text style={styles.statsTitle}>
            {t("weeklyReport.rotationsByFlux")}
          </Text>
          <Text style={styles.statsSubtitle}>
            {t("weeklyReport.rotationsCount", { count: totalRotations })}
          </Text>
          {totalRotations === 0 ? (
            <Text style={styles.body}>
              {t("No collection for the current period")}
            </Text>
          ) : (
            rotationsByFlux.map((item) => (
              <View key={item.flux} style={styles.fluxStatRow}>
                <View
                  style={[
                    styles.fluxStatDot,
                    {
                      backgroundColor:
                        formattedWasteColor(item.flux) || COLORS.teal,
                    },
                  ]}
                />
                <Text style={styles.fluxStatName}>{item.flux}</Text>
                <Text style={styles.fluxStatValue}>
                  {t("weeklyReport.rotationFluxCount", { count: item.count })}
                </Text>
              </View>
            ))
          )}
        </View>
        <View style={styles.statsBlock}>
          <Text style={styles.statsTitle}>{t("weeklyReport.fillingRate")}</Text>
          <Text style={styles.statsSubtitle}>
            {t("weeklyReport.fillingAvgLabel", { avg: overallFillingAvg })}
          </Text>
          {fillingStats.length === 0 ? (
            <Text style={styles.body}>
              {t("No collection for the current period")}
            </Text>
          ) : (
            fillingStats.map((item) => (
              <View key={item.flux} style={styles.fluxStatRow}>
                <View
                  style={[
                    styles.fluxStatDot,
                    {
                      backgroundColor:
                        formattedWasteColor(item.flux) || COLORS.teal,
                    },
                  ]}
                />
                <Text style={styles.fluxStatName}>{item.flux}</Text>
                <Text style={styles.fluxStatValue}>
                  {t("weeklyReport.fillingFluxShort", {
                    min: roundPercent(item.min),
                    max: roundPercent(item.max),
                    avg: roundPercent(item.avg),
                  })}
                </Text>
              </View>
            ))
          )}
        </View>
      </View>
      <Text style={styles.conclusion}>
        <Text style={styles.conclusionLabel}>
          {t("weeklyReport.conclusion")} :{" "}
        </Text>
        {conclusion}
      </Text>
      <ReportFooter t={t} siteName={siteName} flux={fluxLabels.join(", ")} />
    </Page>
  );
};

const fluxQualityLevelLabel = (t, stars) => {
  if (stars == null) return null;
  if (stars >= 3) return t("weeklyReport.fluxQualityLevelGood");
  if (stars === 2) return t("weeklyReport.fluxQualityLevelFair");
  if (stars === 1) return t("weeklyReport.fluxQualityLevelLow");
  return t("weeklyReport.fluxQualityLevelCritical");
};

const buildSortingErrorConclusion = (
  t,
  { flux, slices = [], total = 0, contaminationRate, stars, comparison } = {},
) => {
  const paragraphs = [];
  const topTwo = slices.slice(0, 2);
  const topShare =
    total > 0
      ? roundPercent(
          (topTwo.reduce((sum, item) => sum + (item.count || 0), 0) * 100) /
            total,
        )
      : 0;

  if (contaminationRate != null) {
    paragraphs.push(
      t("weeklyReport.sortingConclusionRate", {
        flux,
        rate: contaminationRate,
        level: fluxQualityLevelLabel(t, stars) || "",
      }),
    );
  }

  if (topTwo.length > 0) {
    const materials = topTwo
      .map((slice) => {
        const pct = total > 0 ? roundPercent((slice.count * 100) / total) : 0;
        return `${t(slice.material)} (${pct} %)`;
      })
      .join(", ");
    paragraphs.push(
      t("weeklyReport.sortingConclusionFocus", {
        materials,
        share: topShare,
      }),
    );
  }

  const mainShift = (comparison?.shifts || [])[0];
  if (mainShift) {
    paragraphs.push(
      t("weeklyReport.sortingConclusionShift", {
        material: t(mainShift.material),
        delta: Math.abs(roundPercent(mainShift.delta)),
        direction:
          mainShift.delta > 0
            ? t("weeklyReport.sortingConclusionUp")
            : t("weeklyReport.sortingConclusionDown"),
      }),
    );
  }

  if (contaminationRate == null) {
    paragraphs.push(t("weeklyReport.sortingConclusionNoVolume"));
  } else if (contaminationRate >= 30) {
    paragraphs.push(t("weeklyReport.sortingConclusionActionCritical"));
  } else if (contaminationRate >= 15) {
    paragraphs.push(t("weeklyReport.sortingConclusionActionWatch"));
  } else if (contaminationRate >= 5) {
    paragraphs.push(t("weeklyReport.sortingConclusionActionMaintain"));
  } else {
    paragraphs.push(t("weeklyReport.sortingConclusionActionGood"));
  }

  return paragraphs.filter(Boolean);
};

const FluxQualityPages = ({
  t,
  model,
  corporationName,
  corporationLogoUrl,
  siteName,
  periodStart,
  periodStop,
  dataVolume,
}) => {
  const compositionsByFlux = Object.fromEntries(
    (model.compositions || []).map((item) => [item.flux, item]),
  );
  const sortingErrorsByFluxMap = Object.fromEntries(
    (model.sortingErrors || []).map((item) => [item.flux, item]),
  );
  const sortingErrorComparisonsByFlux = Object.fromEntries(
    (model.sortingErrorComparisons || []).map((item) => [item.flux, item]),
  );
  const fluxes = [
    ...new Set([
      ...(model.compositions || []).map((item) => item.flux),
      ...(model.sortingErrors || []).map((item) => item.flux),
    ]),
  ];
  if (fluxes.length === 0) return null;

  return fluxes.flatMap((flux) => {
    const composition = compositionsByFlux[flux];
    const sortingErrors = sortingErrorsByFluxMap[flux];
    const chartItem = sortingErrors?.total > 0 ? sortingErrors : composition;
    const chartSlices = (chartItem?.slices || [])
      .filter((slice) => slice.count > 0)
      .slice(0, 8)
      .map((slice) => ({
        ...slice,
        materialLabel: t(slice.material),
      }));
    const total = chartItem?.total || 0;
    const volume = dataVolume?.[flux];
    const stars =
      volume != null
        ? starsCalculation(
            volume.totalVolumeSortingError,
            volume.totalWasteVolume,
          )
        : null;
    const contaminationRate =
      volume?.totalWasteVolume > 0
        ? Math.max(
            0,
            Math.round(
              ((volume.totalVolumeSortingError || 0) * 100) /
                volume.totalWasteVolume,
            ),
          )
        : null;
    const comparison = sortingErrorComparisonsByFlux[flux];
    const isExemplary = isExemplarySortingFlux({
      sortingErrors,
      contaminationRate,
    });
    const conclusionParagraphs = isExemplary
      ? []
      : buildSortingErrorConclusion(t, {
          flux,
          slices: chartSlices,
          total,
          contaminationRate,
          stars,
          comparison,
        });
    const qualityLevel = fluxQualityLevelLabel(t, stars);

    const mainPage = (
      <Page key={`quality-${flux}`} {...FIXED_PAGE_PROPS} style={styles.page}>
        <ReportHeader
          t={t}
          section={t("weeklyReport.sectionQuality")}
          title={`${t(
            isExemplary
              ? "weeklyReport.exemplaryTitle"
              : "weeklyReport.errorsDistributionTitle",
          )} — ${flux}`}
          periodStart={periodStart}
          periodStop={periodStop}
          corporationLogoUrl={corporationLogoUrl}
        />
        <View style={styles.twoCol}>
          {isExemplary ? (
            <View style={styles.exemplaryCard}>
              <Image src={STAR_IMAGES[3]} style={styles.exemplaryStars} />
              <Text style={styles.exemplaryHeading}>
                {t("weeklyReport.exemplaryHeading")}
              </Text>
              <Text style={styles.exemplaryBody}>
                {t("weeklyReport.exemplaryBody", { flux })}
              </Text>
            </View>
          ) : (
            <View style={styles.distributionCard}>
              {chartSlices.length === 0 ? (
                <Text style={styles.muted}>
                  {t("No collection for the current period")}
                </Text>
              ) : (
                <MaterialHorizontalBarChart
                  slices={chartSlices}
                  total={total}
                  width={520}
                  maxItems={8}
                  slotCount={8}
                  rowHeight={28}
                  barThickness={14}
                  labelFontSize={10}
                  valueFontSize={10}
                  paddingLeft={150}
                  maxLabelLength={28}
                />
              )}
            </View>
          )}
          <View style={styles.sideStack}>
            <View style={styles.kpiBox}>
              <View style={styles.kpiMain}>
                <Text style={styles.kpiValue}>
                  {contaminationRate != null ? `${contaminationRate} %` : "—"}
                </Text>
                <Text style={styles.kpiLabel}>
                  {t("weeklyReport.keyIndicatorUndesirable")}
                </Text>
              </View>
              <View style={styles.kpiQualitySection}>
                <Text style={styles.qualityLabel}>
                  {t("weeklyReport.fluxQuality")}
                </Text>
                <View style={styles.qualityStarsRow}>
                  {stars != null ? (
                    <Image
                      src={STAR_IMAGES[Math.min(3, Math.max(0, stars))]}
                      style={styles.starsImage}
                    />
                  ) : null}
                  {qualityLevel ? (
                    <Text style={styles.qualityLevel}>{qualityLevel}</Text>
                  ) : null}
                </View>
                <Text style={styles.qualityHint}>
                  {t("weeklyReport.fluxQualityScaleHint")}
                </Text>
              </View>
            </View>
          </View>
        </View>
        {isExemplary ? null : (
          <View style={{ marginTop: 14, marginBottom: 8 }}>
            <Text style={styles.conclusionLabel}>
              {t("weeklyReport.conclusion")}
            </Text>
            {conclusionParagraphs.map((paragraph) => (
              <Text key={paragraph} style={styles.conclusion}>
                {paragraph}
              </Text>
            ))}
          </View>
        )}
        <ReportFooter t={t} siteName={siteName} flux={flux} />
      </Page>
    );
    return [mainPage];
  });
};

const CharacterizationPages = ({
  t,
  model,
  corporationName,
  corporationLogoUrl,
  siteName,
  periodStart,
  periodStop,
}) => {
  const compositions = model.compositions || [];
  if (compositions.length === 0) return null;

  return compositions.map((composition) => {
    const chartSlices = slicesWithOthers(composition.slices, 12).map(
      (slice) => ({
        ...slice,
        materialLabel: t(slice.material),
      }),
    );

    return (
      <Page
        key={`characterization-${composition.flux}`}
        {...FIXED_PAGE_PROPS}
        style={styles.page}
      >
        <ReportHeader
          t={t}
          section={t("weeklyReport.sectionQuality")}
          title={t("weeklyReport.characterizationTitle")}
          periodStart={periodStart}
          periodStop={periodStop}
          corporationLogoUrl={corporationLogoUrl}
        />
        <View style={styles.characterizationChartCard}>
          <Text style={styles.cardTitle}>
            {t("weeklyReport.detailByCategory")}
            {composition.flux ? ` — ${composition.flux}` : ""}
          </Text>
          {chartSlices.length === 0 ? (
            <Text style={[styles.muted, { marginTop: 8 }]}>
              {t("No collection for the current period")}
            </Text>
          ) : (
            <MaterialHorizontalBarChart
              slices={chartSlices}
              total={composition.total}
              width={720}
              maxItems={12}
              slotCount={Math.max(chartSlices.length, 8)}
              rowHeight={22}
              barThickness={11}
              labelFontSize={9}
              valueFontSize={9}
              paddingLeft={160}
              maxLabelLength={30}
            />
          )}
          <Text style={styles.chartHintRight}>
            {t("weeklyReport.chartMassHint")}
          </Text>
        </View>
        <ReportFooter t={t} siteName={siteName} flux={composition.flux} />
      </Page>
    );
  });
};

const ZOOM_SLOT_COUNT = 3;
const ZOOM_HEIGHT = 96;
const ZOOM_WIDTH = 140;

const FeaturedPhotoPage = ({
  t,
  photo,
  flux,
  corporationName,
  corporationLogoUrl,
  siteName,
}) => {
  const materials = materialsFromAnomalies(photo.wasteObjectsList);
  const exampleAnomalies = selectExampleAnomalies(
    photo.wasteObjectsList,
    ZOOM_SLOT_COUNT,
  );
  const zoomSlots = Array.from({ length: ZOOM_SLOT_COUNT }, (_, index) => {
    const anomaly = exampleAnomalies[index];
    if (!anomaly) return null;
    const zoomBox = getZoomBox(anomaly, photo.imageDimensions);
    if (!zoomBox) return null;
    return {
      anomaly,
      zoomBox,
      zoomData: {
        ...photo,
        ...zoomBox,
        wasteObjectsList: [anomaly],
      },
    };
  });
  const dateLabel = `${formattedDate(photo._time, t, true, false, true)} — ${formattedTime(photo._time)}`;
  const undesirables =
    materials.length > 0
      ? materials
          .slice(0, 4)
          .map((item) => t(item.material))
          .join(", ")
      : "—";

  return (
    <Page {...FIXED_PAGE_PROPS} style={styles.page}>
      <View style={styles.pageHeader}>
        <View style={{ maxWidth: 520 }}>
          <Text style={styles.sectionLabel}>
            {t("weeklyReport.sectionPhotos")}
          </Text>
          <Text style={styles.pageTitle}>
            {t("weeklyReport.photoErrorTitle")}
          </Text>
          <Text style={styles.pageTitleFlux}>{flux}</Text>
        </View>
        <View style={styles.headerRight}>
          <View style={styles.headerClientLogoBox}>
            {corporationLogoUrl ? (
              ShowClientLogo(corporationLogoUrl, styles.headerClientLogo)
            ) : (
              <Text style={styles.headerClientPlaceholder}>
                {t("weeklyReport.clientLogoPlaceholder")}
              </Text>
            )}
          </View>
          <View style={styles.dateBadge}>
            <Text style={styles.dateBadgeText}>{dateLabel}</Text>
          </View>
        </View>
      </View>
      <View style={styles.photoMain}>
        <View style={styles.photoMainImage}>
          <ImageComponent
            data={photo}
            drawMaterial={true}
            height={310}
            materialFillOpacity={0}
            materialStrokeWidth={4}
          />
        </View>
        <View style={styles.photoZoomColumn}>
          {zoomSlots.map((slot, index) =>
            slot ? (
              <View
                key={`${slot.anomaly.cls}-${index}`}
                style={[styles.photoZoomWrap, { width: ZOOM_WIDTH }]}
              >
                <ImageComponent
                  data={slot.zoomData}
                  drawMaterial={true}
                  height={ZOOM_HEIGHT}
                  width={ZOOM_WIDTH}
                  backgroundColor="#FFF"
                  materialFillOpacity={0}
                  materialStrokeWidth={4}
                />
              </View>
            ) : null,
          )}
        </View>
      </View>
      <View style={styles.photoMetaRow}>
        <Text style={styles.photoUndesirable}>
          <Text style={styles.conclusionLabel}>
            {t("weeklyReport.undesirablesPresent")}{" "}
          </Text>
          {undesirables}
        </Text>
        <Text style={styles.photoAction}>
          {t("weeklyReport.photoActionDetected")}
        </Text>
      </View>
      <ReportFooter t={t} siteName={siteName} flux={flux} />
    </Page>
  );
};

const PhotoGridPage = ({
  t,
  photos,
  startIndex = 0,
  flux,
  corporationName,
  corporationLogoUrl,
  siteName,
  periodStart,
  periodStop,
}) => {
  const slots = Array.from(
    { length: PHOTOS_PER_GRID_PAGE },
    (_, index) => photos[index] || null,
  );

  return (
    <Page {...FIXED_PAGE_PROPS} style={styles.page}>
      <ReportHeader
        t={t}
        section={t("weeklyReport.sectionPhotos")}
        title={t("weeklyReport.photosTitle")}
        periodStart={periodStart}
        periodStop={periodStop}
        corporationLogoUrl={corporationLogoUrl}
      />
      <View style={styles.grid}>
        {slots.map((photo, index) =>
          photo ? (
            <View
              key={`${photo._measurement || photo._id}-${photo._time}-${startIndex + index}`}
              style={styles.gridCard}
            >
              <View style={styles.gridPhotoArea}>
                <ImageComponent
                  data={photo}
                  drawMaterial={true}
                  width={GRID_PHOTO_WIDTH}
                  height={GRID_PHOTO_HEIGHT}
                  materialFillOpacity={0}
                  materialStrokeWidth={3}
                />
              </View>
              <Text style={styles.gridCaption}>
                {t("weeklyReport.photoCaption", {
                  index: startIndex + index + 1,
                })}
              </Text>
              <Text style={styles.gridCaptionMuted}>
                {`${formattedDate(photo._time, t, true, false, true)} — ${formattedTime(photo._time)}`}
              </Text>
            </View>
          ) : (
            <View
              key={`empty-${startIndex + index}`}
              style={styles.gridCardEmpty}
            />
          ),
        )}
      </View>
      <ReportFooter t={t} siteName={siteName} flux={flux} />
    </Page>
  );
};

const PhotoPages = ({
  t,
  photosByFlux,
  corporationName,
  corporationLogoUrl,
  siteName,
  periodStart,
  periodStop,
}) => {
  const entries = Object.entries(photosByFlux || {});
  if (entries.length === 0) return null;
  return entries.flatMap(([flux, photos]) => {
    const pages = [];
    // Photo mise en avant : plus d’anomalies, puis plus récente.
    const featured = [...photos].sort((a, b) => {
      const anomalyDiff =
        (Number(b.anomalies) || 0) - (Number(a.anomalies) || 0);
      if (anomalyDiff !== 0) return anomalyDiff;
      return new Date(b._time) - new Date(a._time);
    })[0];
    if (featured) {
      pages.push(
        <FeaturedPhotoPage
          key={`featured-${flux}`}
          t={t}
          photo={featured}
          flux={flux}
          corporationName={corporationName}
          siteName={siteName}
          periodStart={periodStart}
          periodStop={periodStop}
        />,
      );
    }
    if (photos.length > 1) {
      for (let i = 0; i < photos.length; i += PHOTOS_PER_GRID_PAGE) {
        const chunk = photos.slice(i, i + PHOTOS_PER_GRID_PAGE);
        pages.push(
          <PhotoGridPage
            key={`grid-${flux}-${i}`}
            t={t}
            photos={chunk}
            startIndex={i}
            flux={flux}
            corporationName={corporationName}
            corporationLogoUrl={corporationLogoUrl}
            siteName={siteName}
            periodStart={periodStart}
            periodStop={periodStop}
          />,
        );
      }
    }
    return pages;
  });
};

const KeyIndicatorPage = ({ t, dataVolume }) => {
  const rate = computeUndesirableRate(dataVolume);
  if (rate == null) return null;
  return (
    <Page {...FIXED_PAGE_PROPS} style={styles.keyPage}>
      <View style={styles.keyPageShell}>
        <View style={styles.keyContent}>
          <Text style={styles.keyLabel}>
            {t("weeklyReport.keyIndicatorLabel")}
          </Text>
          <Text style={styles.keyValue}>{`${rate} %`}</Text>
          <Text style={styles.keyDesc}>
            {t("weeklyReport.keyIndicatorDesc", { rate })}
          </Text>
        </View>
        <CornerDecor outerFill={COLORS.teal} innerFill={COLORS.yellow} />
        <View style={styles.keyLogo}>
          <Image src={akanthasLogoWhite} style={styles.keyLogoImage} />
        </View>
      </View>
    </Page>
  );
};

const ClosingPage = ({ t, corporationLogoUrl }) => (
  <Page {...FIXED_PAGE_PROPS} style={styles.closingPage}>
    <View style={styles.closingPageShell}>
      <View style={styles.closingTop}>
        <Image src={akanthasLogoWhite} style={styles.closingAkanthasLogo} />
        <View style={styles.closingClientBox}>
          {corporationLogoUrl ? (
            ShowClientLogo(corporationLogoUrl, styles.closingClientLogo)
          ) : (
            <Text style={styles.coverClientPlaceholder}>
              {t("weeklyReport.clientLogoPlaceholder")}
            </Text>
          )}
        </View>
      </View>
      <View style={styles.closingBody}>
        <View style={styles.closingContent}>
          <Text style={styles.closingSlogan}>
            {t("weeklyReport.closingSlogan")}
          </Text>
          <Text style={styles.closingContact}>
            {t("weeklyReport.closingContactLine", {
              name: "Contact Akanthas",
              email: "contact@akanthas.com",
            })}
          </Text>
          <Text style={styles.closingContact}>
            {t("weeklyReport.closingContactLine", {
              name: "Support",
              email: "support@akanthas.com",
            })}
          </Text>
          <Text style={styles.closingUrl}>akanthas.com</Text>
        </View>
        <View style={styles.closingIllustrationCol}>
          <Image src={mascotClosing} style={styles.closingIllustration} />
        </View>
      </View>
      <View style={styles.closingBottom}>
        <Text style={styles.closingFooter}>
          {t("weeklyReport.closingLegal")}
        </Text>
      </View>
    </View>
  </Page>
);

const WeeklyReportPdfDocument = ({
  corporationName,
  corporationLogoUrl,
  siteName,
  weekNumber,
  year,
  periodStart,
  periodStop,
  model,
  photosByFlux,
  dataVolume,
}) => {
  const { t } = useTranslation();
  const fluxLabels = [
    ...new Set([
      ...(model.compositions || []).map((item) => item.flux),
      ...(model.sortingErrors || []).map((item) => item.flux),
      ...(model.rotationsByFlux || []).map((item) => item.flux),
    ]),
  ];
  const hasQuality =
    (model.compositions || []).length > 0 ||
    (model.sortingErrors || []).length > 0;
  const hasPhotos = Object.keys(photosByFlux || {}).length > 0;

  return (
    <Document creator="Akanthas" producer="Akanthas">
      <CoverPage
        t={t}
        corporationLogoUrl={corporationLogoUrl}
        corporationName={corporationName}
        siteName={siteName}
        fluxLabels={fluxLabels}
        weekNumber={weekNumber}
        year={year}
        periodStart={periodStart}
        periodStop={periodStop}
      />
      <SynthesisPage
        t={t}
        model={model}
        corporationLogoUrl={corporationLogoUrl}
        corporationName={corporationName}
        siteName={siteName}
        periodStart={periodStart}
        periodStop={periodStop}
        fluxLabels={fluxLabels}
      />
      <ChapterDivider
        chapter={t("weeklyReport.chapter01")}
        title={t("weeklyReport.sectionCollection")}
        description={t("weeklyReport.chapterCollectionDesc")}
      />
      <RotationsPage
        t={t}
        model={model}
        corporationLogoUrl={corporationLogoUrl}
        corporationName={corporationName}
        siteName={siteName}
        periodStart={periodStart}
        periodStop={periodStop}
        fluxLabels={fluxLabels}
      />
      {hasQuality ? (
        <ChapterDivider
          chapter={t("weeklyReport.chapter02")}
          title={t("weeklyReport.sectionQuality")}
          description={t("weeklyReport.chapterQualityDesc")}
        />
      ) : null}
      <FluxQualityPages
        t={t}
        model={model}
        corporationLogoUrl={corporationLogoUrl}
        corporationName={corporationName}
        siteName={siteName}
        periodStart={periodStart}
        periodStop={periodStop}
        dataVolume={dataVolume}
      />
      {hasPhotos ? (
        <ChapterDivider
          chapter={t("weeklyReport.chapter03")}
          title={t("weeklyReport.sectionPhotos")}
          description={t("weeklyReport.chapterPhotosDesc")}
        />
      ) : null}
      <PhotoPages
        t={t}
        photosByFlux={photosByFlux}
        corporationLogoUrl={corporationLogoUrl}
        corporationName={corporationName}
        siteName={siteName}
        periodStart={periodStart}
        periodStop={periodStop}
      />
      <CharacterizationPages
        t={t}
        model={model}
        corporationLogoUrl={corporationLogoUrl}
        corporationName={corporationName}
        siteName={siteName}
        periodStart={periodStart}
        periodStop={periodStop}
      />
      <KeyIndicatorPage t={t} dataVolume={dataVolume} />
      <ClosingPage t={t} corporationLogoUrl={corporationLogoUrl} />
    </Document>
  );
};

export default WeeklyReportPdfDocument;
